import { defineStore } from 'pinia';
import { isValidEmail } from '~/utils/validation';

/**
 * Maximum number of BCC emails per email type allowed by the API.
 */
const MAX_BCC_EMAILS: number = 50;

/**
 * Maximum length of an individual BCC email allowed by the API.
 */
const MAX_BCC_EMAIL_LENGTH: number = 100;

/**
 * The common Pinia store.
 */
export const useAccountStore = defineStore('account', {
  state: () => ({
    /**
     * The listed accounts.
     */
    accounts: [] as Account[],

    /**
     * The current account, if set.
     */
    account: null as Account | null,

    /**
     * The account's slug error.
     */
    slugError: null as string | null,

    /**
     * The account's complimentary voucher prefix error.
     */
    complimentaryVoucherPrefixError: null as string | null,

    /**
     * Per-entry server validation errors for bcc_emails returned by a 422
     * response, keyed by email type then index (e.g. { receipt: { 0: '...' } }).
     */
    serverBccEmailErrors: {
      receipt: {},
      recipient: {},
      shipped: {},
      payment_link: {},
    } as BccEmailErrors,

    /**
     * Should account fields have validation displayed.
     */
    showValidation: false as boolean,
  }),

  getters: {
    /**
     * Are we operating in an account?
     *
     * @param state
     */
    inAccount: (state): boolean => state.account !== null,

    /**
     * The account's locale.
     *
     * @param state
     */
    locale: (state): string => {
      if (state.account !== null) {
        return state.account.locale;
      }

      return 'en_US';
    },

    /**
     * The current account's validation errors.
     */
    validationErrors: (state): AccountErrors => {
      const errors: AccountErrors = {} as AccountErrors;

      if (state.slugError !== null) {
        errors.slug = state.slugError;
      }

      if (state.complimentaryVoucherPrefixError !== null) {
        errors.complimentary_voucher_prefix =
          state.complimentaryVoucherPrefixError;
      }

      // Always surface server-side per-entry BCC email errors so the user can fix them
      Object.entries(state.serverBccEmailErrors.receipt).forEach(
        ([index, message]) => {
          errors[`bcc_emails.receipt.${Number(index)}`] = message;
        },
      );

      Object.entries(state.serverBccEmailErrors.recipient).forEach(
        ([index, message]) => {
          errors[`bcc_emails.recipient.${Number(index)}`] = message;
        },
      );

      Object.entries(state.serverBccEmailErrors.shipped).forEach(
        ([index, message]) => {
          errors[`bcc_emails.shipped.${Number(index)}`] = message;
        },
      );

      Object.entries(state.serverBccEmailErrors.payment_link).forEach(
        ([index, message]) => {
          errors[`bcc_emails.payment_link.${Number(index)}`] = message;
        },
      );

      // If validation should not be shown
      if (!state.showValidation) {
        // Stop there
        return errors;
      }

      // If no account is set
      if (!state.account) {
        // Stop there
        return errors;
      }

      if (!state.account.slug) {
        errors.slug = 'Enter a sign in URL';
      }

      // Validate the receipt BCC emails
      const receiptEmails: string[] = state.account.bcc_emails?.receipt ?? [];

      if (receiptEmails.length > MAX_BCC_EMAILS) {
        errors['bcc_emails.receipt'] =
          `Enter ${MAX_BCC_EMAILS} email addresses or fewer`;
      }

      const seenReceiptEmails: Set<string> = new Set();
      receiptEmails.forEach((email: string, index: number): void => {
        const key: `bcc_emails.receipt.${number}` = `bcc_emails.receipt.${index}`;

        if (errors[key]) {
          return;
        }

        if (!email) {
          errors[key] = 'Enter an email address';
        } else if (email.length > MAX_BCC_EMAIL_LENGTH) {
          errors[key] =
            `The email address must be ${MAX_BCC_EMAIL_LENGTH} characters or less`;
        } else if (!isValidEmail(email)) {
          errors[key] = 'Enter a valid email address';
        } else if (seenReceiptEmails.has(email)) {
          errors[key] = 'Email addresses must be unique';
        }

        seenReceiptEmails.add(email);
      });

      // Validate the recipient BCC emails
      const recipientEmails: string[] =
        state.account.bcc_emails?.recipient ?? [];

      if (recipientEmails.length > MAX_BCC_EMAILS) {
        errors['bcc_emails.recipient'] =
          `Enter ${MAX_BCC_EMAILS} email addresses or fewer`;
      }

      const seenRecipientEmails: Set<string> = new Set();
      recipientEmails.forEach((email: string, index: number): void => {
        const key: `bcc_emails.recipient.${number}` = `bcc_emails.recipient.${index}`;

        if (errors[key]) {
          return;
        }

        if (!email) {
          errors[key] = 'Enter an email address';
        } else if (email.length > MAX_BCC_EMAIL_LENGTH) {
          errors[key] =
            `The email address must be ${MAX_BCC_EMAIL_LENGTH} characters or less`;
        } else if (!isValidEmail(email)) {
          errors[key] = 'Enter a valid email address';
        } else if (seenRecipientEmails.has(email)) {
          errors[key] = 'Email addresses must be unique';
        }

        seenRecipientEmails.add(email);
      });

      // Validate the shipped BCC emails
      const shippedEmails: string[] = state.account.bcc_emails?.shipped ?? [];

      if (shippedEmails.length > MAX_BCC_EMAILS) {
        errors['bcc_emails.shipped'] =
          `Enter ${MAX_BCC_EMAILS} email addresses or fewer`;
      }

      const seenShippedEmails: Set<string> = new Set();
      shippedEmails.forEach((email: string, index: number): void => {
        const key: `bcc_emails.shipped.${number}` = `bcc_emails.shipped.${index}`;

        if (errors[key]) {
          return;
        }

        if (!email) {
          errors[key] = 'Enter an email address';
        } else if (email.length > MAX_BCC_EMAIL_LENGTH) {
          errors[key] =
            `The email address must be ${MAX_BCC_EMAIL_LENGTH} characters or less`;
        } else if (!isValidEmail(email)) {
          errors[key] = 'Enter a valid email address';
        } else if (seenShippedEmails.has(email)) {
          errors[key] = 'Email addresses must be unique';
        }

        seenShippedEmails.add(email);
      });

      // Validate the payment link BCC emails
      const paymentLinkEmails: string[] =
        state.account.bcc_emails?.payment_link ?? [];

      if (paymentLinkEmails.length > MAX_BCC_EMAILS) {
        errors['bcc_emails.payment_link'] =
          `Enter ${MAX_BCC_EMAILS} email addresses or fewer`;
      }

      const seenPaymentLinkEmails: Set<string> = new Set();
      paymentLinkEmails.forEach((email: string, index: number): void => {
        const key: `bcc_emails.payment_link.${number}` = `bcc_emails.payment_link.${index}`;

        if (errors[key]) {
          return;
        }

        if (!email) {
          errors[key] = 'Enter an email address';
        } else if (email.length > MAX_BCC_EMAIL_LENGTH) {
          errors[key] =
            `The email address must be ${MAX_BCC_EMAIL_LENGTH} characters or less`;
        } else if (!isValidEmail(email)) {
          errors[key] = 'Enter a valid email address';
        } else if (seenPaymentLinkEmails.has(email)) {
          errors[key] = 'Email addresses must be unique';
        }

        seenPaymentLinkEmails.add(email);
      });

      // Validate default validity
      if (Object.keys(validateValidity(state.account.validity)).length) {
        errors.validity = validateValidity(state.account.validity);
      }

      // Validate monetary validity
      if (
        Object.keys(validateValidity(state.account.monetary_validity)).length
      ) {
        errors.monetary_validity = validateValidity(
          state.account.monetary_validity,
        );
      }

      return errors;
    },

    /**
     * Is the account in a valid state.
     */
    isValid(): boolean {
      return Object.keys(this.validationErrors).length === 0;
    },

    /**
     * Is the current account suspended.
     */
    isSuspended: (state): boolean => state.account?.status === 'suspended',

    /**
     * Is the current account restricted.
     */
    isRestricted: (state): boolean => state.account?.status === 'restricted',

    /**
     * Is the current account warned.
     */
    isWarned: (state): boolean => state.account?.status === 'warned',

    /**
     * Is the current account pending deletion.
     */
    isPendingDeletion: (state): boolean =>
      state.account?.status === 'pending_deletion',

    /**
     * Can the current account perform write operations.
     */
    canWrite: (state): boolean =>
      state.account?.status === 'active' || state.account?.status === 'warned',
  },

  actions: {
    /**
     * List accounts.
     */
    async index(): Promise<void> {
      const { data: accounts } = await apiGet('/accounts');

      this.accounts = accounts;
    },

    /**
     * Retrieve an account.
     */
    async retrieve(slug: string): Promise<Account> {
      // Retrieve the account
      const { data } = await apiGet(`/accounts/slug/${slug}`);

      return data;
    },

    /**
     * Update the account.
     */
    async update(): Promise<boolean> {
      const data: Account = this.account as Account;

      // Take a snapshot of the account before the update request
      const oldAccount: Account = this.account as Account;

      // Clear any prior server-side BCC email errors before retrying
      this.serverBccEmailErrors = {
        receipt: {},
        recipient: {},
        shipped: {},
        payment_link: {},
      };

      try {
        // Update the account
        await apiPatch(`/accounts/${data.id}`, {
          name: data.name,
          locale: data.locale,
          currency: data.currency,
          timezone: data.timezone,
          slug: data.slug,
          validity: data.validity,
          monetary_validity: data.monetary_validity,
          tax_percentage: data.tax_percentage,
          monetary_tax_percentage: data.monetary_tax_percentage,
          fulfilment_tax_percentage: data.fulfilment_tax_percentage,
          staff_discount_requires_cost_centre:
            data.staff_discount_requires_cost_centre,
          staff_discount_requires_reason: data.staff_discount_requires_reason,
          has_terminal_card_payments: data.has_terminal_card_payments,
          data_retention_months: data.data_retention_months,
          complimentary_voucher_prefix: data.complimentary_voucher_prefix,
          short_date_format: data.short_date_format,
          voucher_reference_on_resend: data.voucher_reference_on_resend,
          voucher_pool_low_stock_threshold:
            data.voucher_pool_low_stock_threshold,
          bcc_emails: data.bcc_emails,
        });

        // If the update succeeds, the slug is available
        this.slugError = null;

        // If has multiple currencies is true
        if (data.has_multiple_currencies) {
          // Activate the account's has multiple currencies
          await apiPost(`/accounts/${data.id}/multiple-currencies/activate`);
        } else {
          // Otherwise, deactivate the account's has multiple currencies
          await apiPost(`/accounts/${data.id}/multiple-currencies/deactivate`);
        }

        // If has multiple stores is true
        if (data.has_multiple_stores) {
          // Activate the account's has multiple stores
          await apiPost(`/accounts/${data.id}/multiple-stores/activate`);
        } else {
          // Otherwise, deactivate the account's has multiple stores
          await apiPost(`/accounts/${data.id}/multiple-stores/deactivate`);
        }

        // Show a success notification
        notifySuccess('Settings updated');
      } catch (error: any) {
        // Revert the account to its state before the response updated it
        this.account = oldAccount;

        // If the request returns 422
        if (error.statusCode === 422) {
          if (error.data?.errors) {
            // If there is a slug error
            if (hasErrorField(error.data.errors, 'slug')) {
              // If the error is a formatting error
              if (
                error.data.errors.slug.includes('The slug format is invalid.')
              ) {
                // The slug format is invalid
                this.slugError = 'The URL format is invalid';
              }

              // If the error is a uniqueness error
              else if (
                error.data.errors.slug.includes(
                  'The slug has already been taken.',
                )
              ) {
                // The slug is taken
                this.slugError =
                  'This URL is taken. Enter another sign in URL.';
              }
            }

            // If there is a complimentary voucher prefix error
            if (
              hasErrorField(error.data.errors, 'complimentary_voucher_prefix')
            ) {
              // If the error is a formatting error
              if (
                error.data.errors.complimentary_voucher_prefix.includes(
                  'The complimentary voucher prefix format is invalid.',
                )
              ) {
                // The prefix format is invalid
                this.complimentaryVoucherPrefixError =
                  'The prefix format is invalid';
              }
            }

            // Surface server-side per-entry BCC email errors (e.g. bcc_emails.receipt.0)
            const indexed: BccEmailErrors = {
              receipt: {},
              recipient: {},
              shipped: {},
              payment_link: {},
            };

            let hasIndexed: boolean = false;

            Object.entries(
              error.data.errors as Record<string, string[] | string>,
            ).forEach(([key, value]): void => {
              const match = key.match(
                /^bcc_emails\.(receipt|recipient|shipped|payment_link)\.(\d+)$/,
              );

              if (!match) {
                return;
              }

              const type = match[1] as keyof BccEmailErrors;
              const index: number = Number(match[2]);
              indexed[type][index] = Array.isArray(value) ? value[0] : value;
              hasIndexed = true;
            });

            if (hasIndexed) {
              this.serverBccEmailErrors = indexed;
              this.showValidation = true;
            }

            return false;
          }
        }

        throw error;
      }

      return true;
    },

    /**
     * Delete the account.
     */
    async delete(confirmName: string): Promise<void> {
      await apiDelete('/accounts', {
        body: { confirm_name: confirmName },
      });
    },

    /**
     * Check an account slug's availability.
     */
    async checkSlugAvailability(slug: string): Promise<void> {
      // Take a snapshot of the account before the check slug availability request
      const oldAccount: Account = this.account as Account;

      try {
        // Check the slug's availability
        await apiPost(`/accounts/${(this.account as Account).id}/slug`, {
          slug,
        });

        // If it returns 200, the slug is available
        this.slugError = null;
      } catch (error: any) {
        // If the request returns 422
        if (error.statusCode === 422) {
          // If there is a slug error
          if (hasErrorField(error.data?.errors, 'slug')) {
            // If the error is a formatting error
            if (
              error.data.errors.slug.includes('The slug format is invalid.')
            ) {
              // The slug format is invalid
              this.slugError = 'The URL format is invalid';

              return;
            }

            // If the error is a uniqueness error
            if (
              error.data.errors.slug.includes(
                'The slug has already been taken.',
              )
            ) {
              // The slug is taken
              this.slugError = 'This URL is taken. Enter another sign in URL.';

              return;
            }
          }
        }

        throw error;
      } finally {
        // Revert the account to its state before the response updated it
        this.account = oldAccount;
      }
    },
  },
});
